/**
 * Uploader Component
 *
 * Standalone component that displays uploading state with progress bars.
 * Shows active uploads, completed, and failed items with controls.
 */

import { useState, useEffect, useRef } from 'react';
import { ArrowIcon, CrossIcon } from '../../../icons';
import { useMediaLibraryStore } from '../../MediaLibrary/hooks/useMediaLibraryStore';
import { mediaLibraryStore } from '../../MediaLibrary/mediaLibraryStore';
import { MediaLibraryTheme } from '../../MediaLibrary/types';
import type { UploadItem } from './types';
import { UploadItemComponent } from './UploadItem';
import styles from './Uploader.module.scss';

export interface UploaderProps {
  /** List of files being uploaded */
  uploads?: UploadItem[];
  /** Callback to retry a failed upload */
  onRetry?: (uploadId: string) => void;
  /** Callback to remove a completed/failed upload from the list */
  onRemove?: (uploadId: string) => void;
  /** Callback to navigate to uploaded asset and select it */
  onGoToFile?: (assetId: string) => void;
  /** Whether to show "Go to file" button for completed uploads */
  showGoToFile?: boolean;
  /** Callback to open the missing files dialog for a .gltf upload */
  onAddMissingFiles?: (uploadId: string) => void;
  /** Optional theme for styling */
  theme?: MediaLibraryTheme;
  /** Custom CSS class */
  className?: string;
}

export const Uploader = ({
  uploads,
  onRetry,
  onRemove,
  onGoToFile,
  showGoToFile = false,
  onAddMissingFiles,
  theme = 'plain',
  className = '',
}: UploaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimised, setIsMinimised] = useState(false);
  const [activeAddMissingFilesId, setActiveAddMissingFilesId] = useState<string | null>(null);
  const uploadQueue = useMediaLibraryStore(s => s.uploadQueue);
  const _onAddMissingFiles = useMediaLibraryStore(s => s.onAddMissingFiles);
  const _onGoToFile = useMediaLibraryStore(s => s.onGoToFile);
  const _uploads = uploads ?? uploadQueue?.uploads ?? [];
  const hasMissingFilesCountRef = useRef(_uploads.filter(upload => upload.error === 'Missing resource files').length);

  const removeUpload = (uploadId: string) => {
    const uploadQueue = mediaLibraryStore?.getState()?.uploadQueue;
    if (!uploadQueue) return;
    uploadQueue.removeUpload(uploadId);
    uploadQueue.uploads = uploadQueue.uploads?.filter(upload => upload.id !== uploadId);
    mediaLibraryStore.setState({ uploadQueue });
  };

  const _remove = onRemove ?? removeUpload;

  useEffect(() => {
    const currentHasMissingFilesCount = _uploads.filter(upload => upload.error === 'Missing resource files').length;
    if (currentHasMissingFilesCount < hasMissingFilesCountRef.current) {
      setActiveAddMissingFilesId(null);
    }
    hasMissingFilesCountRef.current = currentHasMissingFilesCount;
  }, [_uploads])
  // Auto-open when new uploads are added
  useEffect(() => {
    const hasActiveUploads = _uploads.some(
      upload => upload.status === 'uploading' || upload.status === 'pending' || upload.status === 'error'
    );
    if (hasActiveUploads) {
      setIsOpen(true);
    }
  }, [_uploads.length]);

  const activeCount = _uploads.filter(
    u => u.status === 'uploading' || u.status === 'pending'
  ).length;
  const completedCount = _uploads.filter(u => u.status === 'completed').length;
  const errorCount = _uploads.filter(u => u.status === 'error').length;

  const renderSummaryText = () => {
    if (activeCount > 0) {
      return `Uploading ${activeCount} file${activeCount > 1 ? 's' : ''}`;
    }
    if (errorCount > 0) {
      return `Upload failed`;
    }
    if (completedCount > 0) {
      return `Upload successful`;
    }
    return 'No uploads in progress';
  };

  const removeAll = () => {
    _uploads.forEach(upload => {
      if (upload.status === 'completed' || upload.status === 'error') {
        _remove && _remove(upload.id);
      }
    });
  };

  return (
    // TODO: Only show if any uploads exist
    <details
      open={isOpen}
      onToggle={(e) => {
        if (!!activeAddMissingFilesId) return; // Prevent toggling while add missing files dialog is active
        setIsOpen((e.target as HTMLDetailsElement).open)
      }}
      className={`${styles.uploader} ${isMinimised ? styles.minimisedUploader : ''} ${className} uploader`}
      data-zpr-theme={theme}
    >
      <summary className={styles.summary}>
        <div className={styles.header}>
          <h4 className={styles.title}>
            {renderSummaryText()}
          </h4>
          <div className={styles.btnContainer}>
            <button
              type="button"
              aria-label="Toggle uploader"
              className={`${styles.toggleIcon} ${isMinimised ? styles.minimisedIcon : ''} btn btn-ghost p0`}
              onClick={e => {
                e.preventDefault();
                setIsMinimised(prev => !prev);
              }}
            >
              <ArrowIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
            </button>
            {/* Close button only to be displayed if no uploads are still in progress */}
            {activeCount === 0 && !activeAddMissingFilesId && (
              <button
                className='btn btn-ghost p0'
                aria-label="Close uploader"
                type="button"
                onClick={!!activeAddMissingFilesId ? undefined :  removeAll}
              ><CrossIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" /></button>
            )}
          </div>
        </div>
      </summary>
      <div className={`${styles.contentWrapper} ${isMinimised ? styles.minimised : ''}`}>
        <div className={styles.contentWrapperInner}>
          <div className={styles.content}>
            <div className={styles.list}>
              {_uploads.length === 0 ? (
                <p>No uploads in progress</p>
              ) : _uploads.map((upload) => (
                <UploadItemComponent
                  key={upload.id}
                  upload={upload}
                  // onCancel={onCancel ?? uploadQueue?.cancelUpload}
                  onRetry={onRetry ?? uploadQueue?.retryUpload}
                  onRemove={_remove}
                  onGoToFile={onGoToFile ?? _onGoToFile}
                  onAddMissingFiles={(uploadId) => {
                    setActiveAddMissingFilesId(uploadId);
                    (onAddMissingFiles ?? _onAddMissingFiles)?.(uploadId);
                  }}
                  showGoToFile={showGoToFile}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </details>
  );
};
